// Generated types for LogicalPart
// This file is auto-generated - do not edit manually

import { z } from 'zod';

import { ClosureIntent, LogicalKind, ModularityIntent } from '../enums';

import { ControlPortWriteAPI } from './controlport';
import { InterfacePortWriteAPI } from './interfaceport';
import { PhysicsPortWriteAPI } from './physicsport';
import { RFLPElementReadAPI, RFLPElementWriteAPI } from './rflpelement';
import { RequirementWriteAPI } from './requirement';
import { ControlPortWriteAPISchema } from './controlport';
import { InterfacePortWriteAPISchema } from './interfaceport';
import { PhysicsPortWriteAPISchema } from './physicsport';
import { RFLPElementReadAPISchemaObject, RFLPElementWriteAPISchemaObject } from './rflpelement';
import { RequirementWriteAPISchema } from './requirement';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface LogicalPartReadAPI extends Omit<RFLPElementReadAPI, 'description'> {
  /** What this LogicalPart does in the design and why it exists in the architecture. Required when authoring via the agent (LLM tool surface); optional for direct REST callers. */
  description?: string | null;
  /** COMPOSITE has children; HARDWARE_LEAF and SOFTWARE_LEAF are terminal. */
  kind?: LogicalKind;
  /** Usage multiplicity — how many interchangeable instances of this part the design uses (SysML v2 `part x[N]`). Effective quantity multiplies down the LP chain. */
  quantity?: number;
  /** Leaf closure intent: SOURCED (select a part), BUILD (realised by its own project), OPEN (undecided). */
  closure?: ClosureIntent;
  /** Physical-modularity intent: REQUIRED (LRU — the LP's parts must form a carve-out-able assembly), PREFERRED, or NONE. */
  modularity_intent?: ModularityIntent;
  /** Audit record of the last flowdown sweep: per-requirement dispositions (derived / inherited / not_applicable) with rationales and the fingerprint of each requirement answered. Written by `consider_flowdown`; flowdown staleness is computed from it. */
  flowdown_dispositions?: unknown | null;
}

export interface LogicalPartWriteAPI extends Omit<RFLPElementWriteAPI, 'description'> {
  /** What this LogicalPart does in the design and why it exists in the architecture. Required when authoring via the agent (LLM tool surface); optional for direct REST callers. */
  description?: string | null;
  /** COMPOSITE has children; HARDWARE_LEAF and SOFTWARE_LEAF are terminal. */
  kind?: LogicalKind;
  /** Usage multiplicity — how many interchangeable instances of this part the design uses (SysML v2 `part x[N]`). Effective quantity multiplies down the LP chain. */
  quantity?: number;
  /** Leaf closure intent: SOURCED (select a part), BUILD (realised by its own project), OPEN (undecided). */
  closure?: ClosureIntent;
  /** Physical-modularity intent: REQUIRED (LRU — the LP's parts must form a carve-out-able assembly), PREFERRED, or NONE. */
  modularity_intent?: ModularityIntent;
  /** Audit record of the last flowdown sweep: per-requirement dispositions (derived / inherited / not_applicable) with rationales and the fingerprint of each requirement answered. Written by `consider_flowdown`; flowdown staleness is computed from it. */
  flowdown_dispositions?: unknown | null;
  parent?: string;
  children?: (string | LogicalPartWriteAPI)[];
  /** Actions this LogicalPart performs. */
  performs?: string[];
  /** Parts this LogicalPart is allocated to. */
  allocated_to?: string[];
  /** Requirements owned by this LogicalPart (polymorphic HasRequirement). */
  requirements?: (string | RequirementWriteAPI)[];
  /** Requirements this LogicalPart satisfies. */
  satisfies?: string[];
  /** Behavioural ports (PhysicsPort + ControlPort) owned by this LogicalPart. */
  behavioral_ports?: (string | PhysicsPortWriteAPI | ControlPortWriteAPI)[];
  /** InterfacePort specs owned by this LogicalPart. */
  interface_ports?: (string | InterfacePortWriteAPI)[];
}

// ============================================================================
// Zod Schemas
// ============================================================================

export const LogicalPartReadAPISchema: z.ZodType<LogicalPartReadAPI> = RFLPElementReadAPISchemaObject.omit({ 'description': true }).extend({
  description: z.string().nullable().optional(),
  kind: z.nativeEnum(LogicalKind).optional(),
  quantity: z.number().optional(),
  closure: z.nativeEnum(ClosureIntent).optional(),
  modularity_intent: z.nativeEnum(ModularityIntent).optional(),
  flowdown_dispositions: z.preprocess((v) => typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return v; } })() : v, z.unknown().nullable()).optional(),
}).passthrough();

export const LogicalPartWriteAPISchema: z.ZodType<LogicalPartWriteAPI> = RFLPElementWriteAPISchemaObject.omit({ 'description': true }).extend({
  description: z.string().nullable().optional(),
  kind: z.nativeEnum(LogicalKind).optional(),
  quantity: z.number().optional(),
  closure: z.nativeEnum(ClosureIntent).optional(),
  modularity_intent: z.nativeEnum(ModularityIntent).optional(),
  flowdown_dispositions: z.preprocess((v) => typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return v; } })() : v, z.unknown().nullable()).optional(),
  parent: z.string().optional(),
  children: z.array(z.union([z.string(), z.lazy(() => LogicalPartWriteAPISchema)])).optional(),
  performs: z.array(z.string()).optional(),
  allocated_to: z.array(z.string()).optional(),
  requirements: z.array(z.union([z.string(), z.lazy(() => RequirementWriteAPISchema)])).optional(),
  satisfies: z.array(z.string()).optional(),
  behavioral_ports: z.array(z.union([z.string(), z.lazy(() => PhysicsPortWriteAPISchema), z.lazy(() => ControlPortWriteAPISchema)])).optional(),
  interface_ports: z.array(z.union([z.string(), z.lazy(() => InterfacePortWriteAPISchema)])).optional(),
}).passthrough();
